import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { User } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/client';
import { loadUserRole } from './loadUserRole';
import type { UserRole } from './roleTypes';

export type AuthState = {
    user: User | null;
    /** Firebase auth restoring session */
    loading: boolean;
    /** Resolved admin role; `null` if denied or not applicable */
    role: UserRole | null;
    /** Loading Firestore / claims after user is known */
    roleLoading: boolean;
    /** Authenticated but no valid role document or claim */
    roleDenied: boolean;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

const idleAuth: AuthState = {
    user: null,
    loading: true,
    role: null,
    roleLoading: false,
    roleDenied: false,
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<AuthState>(idleAuth);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (nextUser) => {
            if (!nextUser) {
                setState({
                    user: null,
                    loading: false,
                    role: null,
                    roleLoading: false,
                    roleDenied: false,
                });
                return;
            }

            setState({
                user: nextUser,
                loading: false,
                role: null,
                roleLoading: true,
                roleDenied: false,
            });

            loadUserRole(nextUser)
                .then((role) => {
                    setState((prev) =>
                        prev.user?.uid !== nextUser.uid
                            ? prev
                            : {
                                  ...prev,
                                  role,
                                  roleLoading: false,
                                  roleDenied: !role,
                              }
                    );
                })
                .catch(() => {
                    setState((prev) =>
                        prev.user?.uid !== nextUser.uid
                            ? prev
                            : {
                                  ...prev,
                                  role: null,
                                  roleLoading: false,
                                  roleDenied: true,
                              }
                    );
                });
        });
        return () => unsub();
    }, []);

    const value = useMemo(
        () => ({
            user: state.user,
            loading: state.loading,
            role: state.role,
            roleLoading: state.roleLoading,
            roleDenied: state.roleDenied,
        }),
        [state.user, state.loading, state.role, state.roleLoading, state.roleDenied]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (ctx === undefined) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
}
